import { storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// Función para registrar errores en la base de datos
async function logError(error, context) {
  try {
    console.error("Error en el proceso:", {
      error_type: error.name || "UnknownError",
      error_message: error.message,
      error_code: error.code || "UNKNOWN",
      context: context,
      timestamp: new Date().toISOString(),
      stack_trace: error.stack
    });
  } catch (logError) {
    console.error("Error al intentar registrar el error:", logError);
  }
}

// Función para limpiar el nombre del archivo
const cleanFileName = (fileName) => {
  // Obtener la extensión del archivo
  const extension = fileName.split(".").pop();
  // Obtener el nombre sin la extensión
  const nameWithoutExtension = fileName.slice(0, -(extension.length + 1));
  
  // Reemplazar caracteres especiales y acentos
  const cleanedName = nameWithoutExtension
    .normalize("NFD") // Normaliza los caracteres Unicode
    .replace(/[\u0300-\u036f]/g, "") // Elimina los acentos
    .replace(/[^a-zA-Z0-9]/g, "-") // Reemplaza caracteres especiales con guiones
    .replace(/-+/g, "-") // Reemplaza múltiples guiones con uno solo
    .toLowerCase(); // Convierte a minúsculas
  
  // Retornar el nombre limpio con la extensión
  return `${cleanedName}.${extension}`;
};

// Clases de error personalizadas
class StorageError extends Error {
  constructor(message, code) {
    super(message);
    this.name = "StorageError";
    this.code = code;
  }
}

class ValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = "ValidationError";
  }
}

// Función para generar un nombre único de archivo
const generateUniqueFileName = (originalName) => {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 8);
  const cleanedName = cleanFileName(originalName);
  
  // Obtener la extensión
  const extension = cleanedName.split(".").pop();
  const nameWithoutExt = cleanedName.slice(0, -(extension.length + 1));
  
  // Limitar la longitud del nombre base a 50 caracteres
  const truncatedName = nameWithoutExt.slice(0, 50);
  
  // Construir el nombre final
  const finalName = `${timestamp}-${randomId}-${truncatedName}.${extension}`;
  
  return finalName;
};

export async function uploadFile(file) {
  try {
    // Validación del archivo
    if (!file) {
      const error = new ValidationError("No se ha proporcionado ningún archivo");
      await logError(error, { action: "upload", stage: "validation", fileInfo: null });
      throw error;
    }

    // Verificar que el archivo sea una imagen
    if (!file.type.startsWith("image/")) {
      const error = new ValidationError("Solo se permiten archivos de imagen (JPG, PNG, GIF)");
      await logError(error, { 
        action: "upload", 
        stage: "validation", 
        fileInfo: { 
          type: file.type,
          size: file.size,
          name: file.name 
        } 
      });
      throw error;
    }

    // Verificar tamaño máximo (20MB)
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB en bytes
    if (file.size > MAX_FILE_SIZE) {
      const error = new ValidationError("El archivo excede el tamaño máximo permitido de 20MB");
      await logError(error, { 
        action: "upload", 
        stage: "validation", 
        fileInfo: { 
          size: file.size,
          maxSize: MAX_FILE_SIZE 
        } 
      });
      throw error;
    }
    
    const fileName = generateUniqueFileName(file.name);
    const storageRef = ref(storage, `uploads-publics/${fileName}`);
    
    try {
      // Subir el archivo con metadatos
      const metadata = {
        cacheControl: "3600",
        contentType: file.type
      };
      
      const snapshot = await uploadBytes(storageRef, file, metadata);
      
      // Obtener la URL de descarga
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        fileName,
        url: downloadURL
      };
    } catch (error) {
      let storageError;
      if (error.code === "storage/unauthorized") {
        storageError = new StorageError("No tienes permiso para subir archivos", "UNAUTHORIZED");
      } else if (error.code === "storage/quota-exceeded") {
        storageError = new StorageError("Se ha excedido la cuota de almacenamiento", "QUOTA_EXCEEDED");
      } else if (error.code === "storage/retry-limit-exceeded") {
        storageError = new StorageError("Ya existe un archivo con el mismo nombre", "DUPLICATE_FILE");
      } else {
        storageError = new StorageError(`Error al subir el archivo: ${error.message}`, "UPLOAD_FAILED");
      }
      
      await logError(storageError, { 
        action: "upload", 
        stage: "storage", 
        fileInfo: { 
          fileName,
          size: file.size,
          type: file.type 
        },
        firebaseError: error 
      });
      
      throw storageError;
    }
  } catch (error) {
    console.error("Error en el proceso de subida:", error);
    
    // Si el error no ha sido registrado previamente, lo registramos aquí
    if (!error.logged) {
      await logError(error, { 
        action: "upload", 
        stage: "unknown", 
        fileInfo: file ? { 
          type: file.type,
          size: file.size,
          name: file.name 
        } : null 
      });
    }
    
    // Manejo específico de errores según el tipo
    if (error instanceof ValidationError) {
      throw new Error(`Error de validación: ${error.message}`);
    } else if (error instanceof StorageError) {
      throw new Error(`Error de almacenamiento: ${error.message}`);
    } else {
      throw new Error(`Error inesperado: ${error.message}`);
    }
  }
} 